import { useEffect, useMemo, useState } from 'react'
import { CheckCircle2, Clock3, Loader2, PackageSearch } from 'lucide-react'
import { type BackgroundJob } from '../stores/backgroundJobsStore'
import { cn } from '../lib/cn'
import { Button, Modal } from './ui'

interface PriceIndexJobProgressModalProps {
  job: BackgroundJob | null
  isOpen: boolean
  onClose: () => void
  /** Прерывание задания. Без обработчика кнопка «Прервать» не показывается. */
  onCancel?: () => void
  cancelling?: boolean
}

function formatRetryCountdown(milliseconds: number): string {
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

function calculateStepsProgress(
  steps: Array<{ status: string; progress?: { current: number; total: number } }>,
  status?: string,
  fallback = 0,
): number {
  if (status === 'completed') return 100
  if (!steps.length) return fallback
  const completedShare = steps.reduce((sum, step) => {
    if (step.status === 'completed') return sum + 1
    if (step.status !== 'processing' && step.status !== 'failed') return sum
    const current = Number(step.progress?.current || 0)
    const total = Number(step.progress?.total || 0)
    return sum + (total > 0 ? Math.min(1, Math.max(0, current / total)) : 0)
  }, 0)
  return Math.round((completedShare / steps.length) * 100)
}

function getPipelineStepGroup(
  stepName: string,
): 'Импорт файла' | 'Проверка товаров' | 'Расчёт цены' | 'Формирование файла' {
  if (
    stepName === 'Загрузка строк файла'
    || stepName === 'Сопоставление с каталогами WB и Ozon'
  ) {
    return 'Импорт файла'
  }
  if (stepName === 'Расчёт и сохранение цен') return 'Расчёт цены'
  if (stepName.startsWith('Формирование')) return 'Формирование файла'
  return 'Проверка товаров'
}

function getModalTitle(job: BackgroundJob | null): string {
  if (job?.type === 'price_index_calculate') return 'Расчёт цен'
  if (job?.type === 'price_index_import') return 'Загрузка, проверка и расчёт цен'
  if (job?.type === 'price_index_export') return 'Формирование файла'
  return 'Проверка товаров'
}

export default function PriceIndexJobProgressModal({
  job,
  isOpen,
  onClose,
  onCancel,
  cancelling = false,
}: PriceIndexJobProgressModalProps) {
  const [clock, setClock] = useState(() => Date.now())
  const status = job?.status
  const isRunning = Boolean(job && ['pending', 'processing'].includes(job.status) && !job.cancelled)
  const steps = job?.steps || []
  const overallPercentage = calculateStepsProgress(
    steps,
    status,
    job?.progress?.percentage ?? 0,
  )
  const completedSteps = steps.filter((step) => step.status === 'completed').length
  const currentStepNumber = steps.length
    ? Math.min(
        steps.length,
        Math.max(
          1,
          steps.findIndex((step) => step.status === 'processing' || step.status === 'failed') + 1
            || completedSteps + 1,
        ),
      )
    : 0
  const stepGroups = useMemo(() => steps.reduce<Array<{
    name: ReturnType<typeof getPipelineStepGroup>
    steps: typeof steps
  }>>((groups, step) => {
    const name = getPipelineStepGroup(step.name)
    const previous = groups[groups.length - 1]
    if (previous?.name === name) previous.steps.push(step)
    else groups.push({ name, steps: [step] })
    return groups
  }, []), [steps])

  const rateLimitUntilValue = job?.metadata?.rateLimitUntil
  const rateLimitProvider = String(job?.metadata?.rateLimitProvider || 'Маркетплейс')
  const rateLimitUntilMs = rateLimitUntilValue ? Date.parse(String(rateLimitUntilValue)) : 0
  const rateLimitRemainingMs = Number.isFinite(rateLimitUntilMs)
    ? Math.max(0, rateLimitUntilMs - clock)
    : 0
  const isRateLimited = Boolean(
    job?.metadata?.rateLimited
    && status
    && ['pending', 'processing'].includes(status)
    && rateLimitRemainingMs > 0,
  )

  useEffect(() => {
    if (!rateLimitUntilValue || !status || !['pending', 'processing'].includes(status)) return
    setClock(Date.now())
    const timer = window.setInterval(() => setClock(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [rateLimitUntilValue, status])

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={getModalTitle(job)}
      subtitle={job?.name || 'Задание запускается…'}
      size="lg"
      closeOnOverlayClick={Boolean(job) && !isRunning}
      footer={(
        <>
          {isRunning && onCancel && (
            <Button variant="danger" onClick={onCancel} loading={cancelling} disabled={cancelling}>
              {cancelling ? 'Прерываем…' : 'Прервать'}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {isRunning ? 'Свернуть' : 'Закрыть'}
          </Button>
        </>
      )}
    >
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          {isRateLimited ? (
            <Clock3 className="h-6 w-6 text-amber-600" />
          ) : !job || ['pending', 'processing'].includes(job.status) ? (
            <Loader2 className="h-6 w-6 animate-spin text-indigo-600" />
          ) : job.status === 'completed' ? (
            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
          ) : (
            <PackageSearch className="h-6 w-6 text-red-600" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-gray-800 dark:text-zinc-100">
                {job?.currentStep || 'Подготовка проверки'}
              </span>
              <span className="tabular-nums text-gray-500">{overallPercentage}%</span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-zinc-800">
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-300',
                  job?.status === 'failed' ? 'bg-red-500' : 'bg-indigo-600',
                )}
                style={{ width: `${Math.max(2, overallPercentage)}%` }}
              />
            </div>
            {steps.length > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Этап {currentStepNumber} из {steps.length}
                {completedSteps > 0 && <> · завершено {completedSteps}</>}
              </p>
            )}
            {job?.progress?.total > 0 && (
              <p className="mt-1 text-xs tabular-nums text-gray-500">
                Текущий этап: {job.progress.current.toLocaleString('ru-RU')} из{' '}
                {job.progress.total.toLocaleString('ru-RU')}
              </p>
            )}
          </div>
        </div>

        {isRateLimited && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
            <Clock3 className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="min-w-0">
              <p className="text-sm font-medium">
                {rateLimitProvider} временно ограничил частоту запросов
              </p>
              <p className="mt-0.5 text-xs">
                Проверка продолжится автоматически через{' '}
                <span className="font-semibold tabular-nums">
                  {formatRetryCountdown(rateLimitRemainingMs)}
                </span>
                {rateLimitUntilMs > 0 && (
                  <> — примерно в {new Date(rateLimitUntilMs).toLocaleTimeString('ru-RU', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}</>
                )}
                . Закрывать окно или перезапускать проверку не требуется.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {stepGroups.map((group, groupIndex) => (
            <section key={`${group.name}-${groupIndex}`} className="space-y-2">
              <div className="flex items-center justify-between px-1">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">
                  {group.name}
                </h4>
                <span className="text-xs tabular-nums text-gray-400">
                  {group.steps.filter((step) => step.status === 'completed').length}/{group.steps.length}
                </span>
              </div>
              {group.steps.map((step, index) => (
                <div
                  key={`${step.name}-${index}`}
                  className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2.5 dark:border-zinc-800"
                >
                  {step.status === 'completed' ? (
                    <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                  ) : step.status === 'processing' ? (
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-indigo-600" />
                  ) : step.status === 'failed' ? (
                    <PackageSearch className="h-4 w-4 shrink-0 text-red-600" />
                  ) : (
                    <span className="h-4 w-4 shrink-0 rounded-full border-2 border-gray-300 dark:border-zinc-600" />
                  )}
                  <span className="flex-1 text-sm text-gray-700 dark:text-zinc-200">
                    {step.name}
                  </span>
                  {step.progress && (
                    <span className="text-xs tabular-nums text-gray-500">
                      {step.progress.current}/{step.progress.total}
                    </span>
                  )}
                </div>
              ))}
            </section>
          ))}
          {!job && (
            <div className="py-6 text-center text-sm text-gray-500">
              Ожидаем данные задания…
            </div>
          )}
          {job && steps.length === 0 && (
            <div className="py-4 text-center text-sm text-gray-500">
              Подготавливаем список этапов…
            </div>
          )}
        </div>

        {job?.error && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
            {job.error}
          </div>
        )}
      </div>
    </Modal>
  )
}
